import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Search } from 'lucide-react'
import { useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { POST_TYPE_LABELS, formatDateLong, type PostType } from '@paroquia/shared'
import { Alert, Button, Input, PageLoader } from '@/components/ui/primitives'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { ApiError, apiGet } from '@/lib/api'
import { cn } from '@/lib/utils'
import type { Paginado, PostCard, PostDetail, Taxonomia } from '@/lib/portal-types'
import { GaleriaDeFotos } from './GaleriaDeFotos'
import { CartaoDeNoticia, ListaDeAnexos, Vazio } from './components'

const TIPOS: { valor: PostType | ''; label: string }[] = [
  { valor: '', label: 'Todos' },
  { valor: 'NOTICIA', label: 'Noticias' },
  { valor: 'AVISO', label: 'Avisos' },
  { valor: 'COMUNICADO', label: 'Comunicados' },
  { valor: 'NOTA_PESAR', label: 'Notas de pesar' },
]

/** Listagem publica com busca, filtro por tipo, categoria e tag (RF01.2). */
export function NoticiasPage() {
  const [params, setParams] = useSearchParams()
  const { categoria, tag } = useParams()

  const page = Number(params.get('page') ?? 1)
  const tipo = (params.get('tipo') ?? '') as PostType | ''
  const busca = params.get('q') ?? ''

  const [textoBusca, setTextoBusca] = useState(busca)

  const { data: taxonomia } = useQuery({
    queryKey: ['portal', 'taxonomia'],
    queryFn: () => apiGet<Taxonomia>('/api/portal/taxonomia'),
    staleTime: 5 * 60 * 1000,
  })

  const { data, isLoading, error } = useQuery({
    queryKey: ['portal', 'noticias', { page, tipo, busca, categoria, tag }],
    queryFn: () =>
      apiGet<Paginado<PostCard>>('/api/portal/noticias', {
        page,
        perPage: 12,
        type: tipo || undefined,
        q: busca || undefined,
        category: categoria,
        tag,
      }),
  })

  const categoriaAtual = taxonomia?.categorias.find((c) => c.slug === categoria)
  const titulo = categoriaAtual
    ? categoriaAtual.name
    : tag
      ? `Assunto: ${tag}`
      : busca
        ? `Busca: ${busca}`
        : 'Noticias'

  useDocumentTitle(titulo)

  /** Preserva o recorte atual ao trocar de pagina ou de filtro. */
  function atualizar(mudancas: Record<string, string | number | undefined>) {
    const novo = new URLSearchParams(params)
    for (const [chave, valor] of Object.entries(mudancas)) {
      if (valor === undefined || valor === '') novo.delete(chave)
      else novo.set(chave, String(valor))
    }
    // Trocar filtro sempre volta para a primeira pagina: continuar na pagina 4
    // de um recorte novo costuma cair em "nenhum resultado".
    if (!('page' in mudancas)) novo.delete('page')
    setParams(novo)
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <h1 className="font-serif text-3xl font-semibold">{titulo}</h1>
      {categoriaAtual?.description && (
        <p className="mt-1 text-suave">{categoriaAtual.description}</p>
      )}

      <form
        role="search"
        className="mt-6 flex gap-2"
        onSubmit={(event) => {
          event.preventDefault()
          atualizar({ q: textoBusca })
        }}
      >
        <label htmlFor="busca-noticias" className="sr-only">
          Buscar nas publicacoes
        </label>
        <Input
          id="busca-noticias"
          type="search"
          value={textoBusca}
          onChange={(event) => setTextoBusca(event.target.value)}
          placeholder="Buscar por palavra..."
          className="max-w-sm"
        />
        <Button type="submit" variant="secondary">
          <Search aria-hidden="true" />
          Buscar
        </Button>
      </form>

      {!categoria && !tag && (
        <div role="group" aria-label="Filtrar por tipo" className="mt-4 flex flex-wrap gap-2">
          {TIPOS.map((opcao) => (
            <button
              key={opcao.valor}
              type="button"
              onClick={() => atualizar({ tipo: opcao.valor })}
              aria-pressed={tipo === opcao.valor}
              className={cn(
                'rounded-full border px-3 py-1.5 text-sm font-medium',
                tipo === opcao.valor
                  ? 'border-primario bg-primario text-sobre-primario'
                  : 'border-borda hover:bg-afundado',
              )}
            >
              {opcao.label}
            </button>
          ))}
        </div>
      )}

      {taxonomia && taxonomia.categorias.length > 0 && (
        <nav aria-label="Categorias" className="mt-3 flex flex-wrap gap-2 text-sm">
          {taxonomia.categorias.map((cat) => (
            <Link
              key={cat.slug}
              to={`/noticias/categoria/${cat.slug}`}
              className={cn(
                'rounded-full border px-3 py-1.5 font-medium',
                categoria === cat.slug
                  ? 'border-primario bg-primario-suave'
                  : 'border-borda hover:bg-afundado',
              )}
            >
              {cat.name} ({cat.postCount})
            </Link>
          ))}
        </nav>
      )}

      {isLoading && <PageLoader />}

      {error && (
        <Alert tone="danger" className="mt-6">
          {error instanceof ApiError ? error.message : 'Nao foi possivel carregar as publicacoes.'}
        </Alert>
      )}

      {data && data.items.length === 0 && (
        <div className="mt-6">
          <Vazio
            titulo="Nenhuma publicacao encontrada"
            texto={
              busca
                ? 'Tente outra palavra ou remova os filtros.'
                : 'Assim que houver publicacoes, elas aparecem aqui.'
            }
          />
        </div>
      )}

      {data && data.items.length > 0 && (
        <>
          <p className="mt-6 text-sm text-suave" aria-live="polite">
            {data.total} {data.total === 1 ? 'publicacao' : 'publicacoes'}
          </p>

          <div className="mt-3 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {data.items.map((post) => (
              <CartaoDeNoticia key={post.id} post={post} />
            ))}
          </div>

          {data.totalPages > 1 && (
            <nav aria-label="Paginacao" className="mt-8 flex items-center justify-center gap-3">
              <Button
                variant="outline"
                disabled={page <= 1}
                onClick={() => atualizar({ page: page - 1 })}
              >
                Anterior
              </Button>
              <span className="text-sm">
                Pagina {data.page} de {data.totalPages}
              </span>
              <Button
                variant="outline"
                disabled={page >= data.totalPages}
                onClick={() => atualizar({ page: page + 1 })}
              >
                Proxima
              </Button>
            </nav>
          )}
        </>
      )}
    </div>
  )
}

/** Detalhe da publicacao, com anexos e relacionadas. */
export function NoticiaDetalhePage() {
  const { slug } = useParams()

  const { data, isLoading, error } = useQuery({
    queryKey: ['portal', 'noticia', slug],
    queryFn: () => apiGet<PostDetail>(`/api/portal/noticias/${slug}`),
    enabled: !!slug,
  })

  useDocumentTitle(data?.title)

  if (isLoading) return <PageLoader />

  if (error) {
    const naoEncontrada = error instanceof ApiError && error.status === 404
    return (
      <div className="mx-auto max-w-3xl px-4 py-12 text-center">
        <h1 className="font-serif text-2xl font-semibold">
          {naoEncontrada ? 'Publicacao nao encontrada' : 'Nao foi possivel abrir a publicacao'}
        </h1>
        <p className="mt-2 text-suave">
          {naoEncontrada
            ? 'O endereco pode ter mudado, ou a publicacao foi removida.'
            : 'Verifique sua conexao e tente novamente.'}
        </p>
        <Link to="/noticias" className="mt-6 inline-block font-medium underline">
          Ver todas as publicacoes
        </Link>
      </div>
    )
  }
  if (!data) return null

  return (
    <article className="mx-auto max-w-3xl px-4 py-8">
      <Link
        to="/noticias"
        className="nao-imprimir inline-flex items-center gap-1.5 text-sm font-medium underline"
      >
        <ArrowLeft className="size-4" aria-hidden="true" />
        Todas as publicacoes
      </Link>

      <header className="mt-4">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {data.type !== 'NOTICIA' && (
            <span className="rounded bg-primario-suave px-2 py-0.5 font-semibold text-primario">
              {POST_TYPE_LABELS[data.type]}
            </span>
          )}
          {data.category && (
            <Link
              to={`/noticias/categoria/${data.category.slug}`}
              className="font-medium underline"
              style={data.category.color ? { color: data.category.color } : undefined}
            >
              {data.category.name}
            </Link>
          )}
        </div>

        <h1 className="mt-2 font-serif text-3xl font-semibold sm:text-4xl">{data.title}</h1>

        <p className="mt-3 text-sm text-suave">
          {data.publishedAt && (
            <time dateTime={data.publishedAt}>{formatDateLong(data.publishedAt)}</time>
          )}
          {data.author && <span> — por {data.author.name}</span>}
        </p>

        {data.excerpt && <p className="mt-4 text-lg text-suave">{data.excerpt}</p>}
      </header>

      {data.cover && (
        <figure className="mt-6">
          <img
            src={data.cover.large}
            alt={data.cover.alt ?? ''}
            className="w-full rounded-lg object-cover"
          />
          {data.cover.alt && (
            <figcaption className="mt-2 text-sm text-suave">
              {data.cover.alt}
            </figcaption>
          )}
        </figure>
      )}

      {/*
       * O HTML vem do editor e foi sanitizado NO SERVIDOR, na gravacao
       * (apps/api/src/lib/html.ts). Nao ha saneamento no cliente de proposito:
       * dois pontos de saneamento divergem com o tempo, e o do servidor e o
       * unico que nao pode ser burlado.
       */}
      <div
        className="conteudo-rico mt-8"
        dangerouslySetInnerHTML={{ __html: data.body }}
      />

      <ListaDeAnexos anexos={data.attachments} />

      <GaleriaDeFotos fotos={data.galeria} titulo="Fotos desta publicacao" />

      {data.tags.length > 0 && (
        <nav aria-label="Assuntos" className="nao-imprimir mt-8 flex flex-wrap gap-2">
          {data.tags.map((tag) => (
            <Link
              key={tag.slug}
              to={`/noticias/assunto/${tag.slug}`}
              className="rounded-full border border-borda px-3 py-1 text-sm hover:bg-afundado"
            >
              {tag.name}
            </Link>
          ))}
        </nav>
      )}

      {data.relacionadas.length > 0 && (
        <section className="nao-imprimir mt-12">
          <h2 className="font-serif text-xl font-semibold">Leia tambem</h2>
          <div className="mt-4 grid gap-5 sm:grid-cols-3">
            {data.relacionadas.map((post) => (
              <CartaoDeNoticia key={post.id} post={post} />
            ))}
          </div>
        </section>
      )}
    </article>
  )
}
